import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import styles from './HeadshotLanding.module.css';

import PricingSection from '@/components/headshots/PricingSection';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

const imageRoot =
  'https://media.easy-peasy.ai/landing/headshots/studio-2026-09-15';
const portraits = [
  {
    file: 'executive-woman',
    category: 'Studio',
    label: 'Quiet confidence',
    alt: 'A silver-haired woman in a navy shirt against a slate studio backdrop',
  },
  {
    file: 'woman-office',
    category: 'Daylight',
    label: 'A fresh perspective',
    alt: 'A woman in an ivory blazer in a softly lit office',
  },
  {
    file: 'creative-man',
    category: 'Creative',
    label: 'A little character',
    alt: 'A man with glasses in a brown blazer against a plaster wall',
  },
  {
    file: 'outdoor-man',
    category: 'Daylight',
    label: 'Naturally approachable',
    alt: 'A smiling man in a pale blue shirt in a leafy courtyard',
  },
  {
    file: 'creative-woman',
    category: 'Creative',
    label: 'Warm and confident',
    alt: 'A woman with short natural curls against a terracotta backdrop',
  },
  {
    file: 'woman-creative',
    category: 'Studio',
    label: 'Your own style',
    alt: 'A woman in a brown blouse against a sage studio backdrop',
  },
  {
    file: 'urban-turtleneck',
    category: 'Urban',
    label: 'City perspective',
    alt: 'A man in a black turtleneck with city architecture behind him',
  },
  {
    file: 'marina-sunset',
    category: 'Marina',
    label: 'By the waterfront',
    alt: 'A woman in a grey suit beside a marina at sunset',
  },
  {
    file: 'monochrome-editorial',
    category: 'Black & white',
    label: 'Timeless monochrome',
    alt: 'A black and white portrait of a woman in a dark shirt against textured concrete',
  },
  {
    file: 'library-study',
    category: 'Library',
    label: 'The library portrait',
    alt: 'A man with glasses and a tweed jacket in a warmly lit library',
  },
  {
    file: 'rooftop-twilight',
    category: 'Rooftop',
    label: 'After hours',
    alt: 'A silver-haired woman in evening wear on a rooftop with city lights at twilight',
  },
  {
    file: 'keynote-speaker',
    category: 'Keynote',
    label: 'Ready for the stage',
    alt: 'A woman in a tailored dark outfit backstage at a conference venue',
  },
  {
    file: 'garden-denim',
    category: 'Garden',
    label: 'Garden in bloom',
    alt: 'A smiling woman in a denim jacket surrounded by garden flowers',
  },
  {
    file: 'white-suit',
    category: 'White studio',
    label: 'All in white',
    alt: 'A woman with short natural curls wearing a white suit against a white studio backdrop',
  },
  {
    file: 'black-tie',
    category: 'Formal',
    label: 'Black tie',
    alt: 'A man with a neatly trimmed beard wearing a tuxedo in a portrait studio',
  },
  {
    file: 'design-studio',
    category: 'Design studio',
    label: 'The creative studio',
    alt: 'A woman with a dark bob in smart casual clothing in a plant-filled design studio',
  },
  {
    file: 'architectural-columns',
    category: 'Architecture',
    label: 'Architectural calm',
    alt: 'A man in business casual clothing beneath contemporary architectural columns',
  },
  {
    file: 'color-backdrops',
    category: 'Color studio',
    label: 'A study in color',
    alt: 'A man with glasses in a modern blazer against layered blue and terracotta backdrops',
  },
  {
    file: 'hotel-lounge',
    category: 'Hotel lounge',
    label: 'Morning light',
    alt: 'A silver-haired woman in navy business attire beside tall windows in a hotel lounge',
  },
  {
    file: 'blue-teal-studio',
    category: 'Studio',
    label: 'A deeper shade',
    alt: 'A man in a charcoal suit and white shirt against a deep blue and teal studio backdrop',
  },
  {
    file: 'polo-studio',
    category: 'Business casual',
    label: 'Easygoing professional',
    alt: 'A man wearing a polo shirt against a softly blurred studio backdrop',
  },
  {
    file: 'window-knit',
    category: 'Window light',
    label: 'By the window',
    alt: 'A silver-haired woman in a knit top seated beside a white wall in natural window light',
  },
  {
    file: 'forest-studio',
    category: 'Studio',
    label: 'Forest tones',
    alt: 'A man with glasses in tailored charcoal clothing against a deep forest-green studio backdrop',
  },
  {
    file: 'graffiti-turtleneck',
    category: 'Street style',
    label: 'Street character',
    alt: 'A woman with short natural curls in a black turtleneck beside a graffiti wall',
  },
];
const faqs = [
  'what_photos',
  'processing_time',
  'ownership',
  'photo_usage',
  'dissatisfaction',
  'misunderstanding',
] as const;

export function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      aria-hidden='true'
    >
      <path d={diagonal ? 'M6 18 18 6M6 6h12v12' : 'M4 12h15m-6-6 6 6-6 6'} />
    </svg>
  );
}

function Portrait({
  file,
  alt,
  className,
  eager = false,
  sizes = '(max-width: 640px) 45vw, 30vw',
}: {
  file: string;
  alt: string;
  className?: string;
  eager?: boolean;
  sizes?: string;
}) {
  return (
    <Image
      src={`${imageRoot}/${file}.webp`}
      alt={alt}
      width={1122}
      height={1402}
      sizes={sizes}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      className={className}
    />
  );
}

export default function HeadshotLanding() {
  const { t, lang } = useTranslation('ai-headshot-generator');

  return (
    <div className={styles.page} lang={lang}>
      <Seo
        templateTitle={t(
          'AI professional headshots. Look like you, on your best day.',
        )}
        description={t(
          'Turn everyday photos into professional AI headshots for LinkedIn, your website, and your next opportunity. Packages from $19. One-time payment.',
        )}
        image={`${imageRoot}/studio-woman.webp`}
        structuredData={false}
      />
      <a href='#headshot-content' className={styles.skipLink}>
        {t('Skip to content')}
      </a>
      <LandingPageNavigation />
      <main id='headshot-content' dir={lang === 'ar' ? 'rtl' : undefined}>
        <div className={styles.container}>
          <nav
            className={styles.sectionNav}
            aria-label={t('Headshot navigation')}
          >
            <span className={styles.studioName}>
              <span aria-hidden='true' />
              {t('The portrait studio')}
            </span>
            <div>
              <a href='#styles'>{t('The looks')}</a>
              <a href='#how-it-works'>{t('How it works')}</a>
              <a href='#pricing'>
                {t('Pricing')} <Arrow diagonal />
              </a>
            </div>
          </nav>
          <section className={styles.hero} aria-labelledby='headshot-title'>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>{t('AI PROFESSIONAL HEADSHOTS')}</p>
              <h1 id='headshot-title'>
                {t('Look like you.')}
                <em>{t('On your best day.')}</em>
              </h1>
              <p className={styles.lede}>
                {t(
                  'Turn a few everyday photos into professional headshots for LinkedIn, your website, and whatever comes next.',
                )}
              </p>
              <a className={styles.primaryButton} href='#pricing'>
                {t('Create my headshots')}
                <Arrow />
              </a>
              <p className={styles.purchaseNote}>
                {t('From $19 · One-time payment · Ready in 1–3 hours')}
              </p>
              <div className={styles.heroFootnote}>
                <span className={styles.smallRule} />
                {t(
                  'Your expression. Your character. A fresh first impression.',
                )}
              </div>
            </div>
            <div className={styles.heroArtwork}>
              <div className={styles.heroPhotos}>
                <figure className={styles.heroMain}>
                  <Portrait
                    file='studio-woman'
                    alt={t(
                      'A smiling woman in a charcoal blazer against a warm studio backdrop',
                    )}
                    eager
                    sizes='(max-width: 700px) 62vw, 31vw'
                  />
                  <figcaption>
                    <span>{t('A new point of view')}</span>
                    <span>01 / 03</span>
                  </figcaption>
                </figure>
                <div className={styles.heroSide}>
                  <Portrait
                    file='studio-man'
                    alt={t(
                      'A man in a navy jacket against a warm sand studio backdrop',
                    )}
                    sizes='(max-width: 700px) 30vw, 16vw'
                  />
                  <Portrait
                    file='daylight-woman'
                    alt={t(
                      'A woman with a dark bob wearing an ivory linen blazer',
                    )}
                    sizes='(max-width: 700px) 30vw, 16vw'
                  />
                </div>
                <div className={styles.photoStamp} aria-hidden='true'>
                  <span>Easy-Peasy.AI</span>
                  <strong>{t('Made for you.')}</strong>
                </div>
              </div>
              <p className={styles.imageNote}>
                {t('Illustrative AI portraits. Your results will vary.')}
              </p>
            </div>
          </section>
          <div className={styles.useCases}>
            <p>{t('A good first impression, everywhere.')}</p>
            <ul>
              <li>LinkedIn</li>
              <li>{t('Company websites')}</li>
              <li>{t('Speaker bios')}</li>
              <li>{t('Your next chapter')}</li>
            </ul>
          </div>
        </div>

        <section
          id='styles'
          className={`${styles.container} ${styles.gallerySection}`}
          aria-labelledby='styles-title'
        >
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>{t('FIND YOUR LOOK')}</p>
              <h2 id='styles-title'>
                {t('Professional has')} <em>{t('a personal side.')}</em>
              </h2>
            </div>
            <p>
              {t(
                'From classic studio portraits to city rooftops and waterfront sunsets. Find a setting that feels like you.',
              )}
            </p>
          </div>
          <div className={styles.gallery}>
            {portraits.map((portrait) => (
              <figure key={portrait.file}>
                <div className={styles.galleryImage}>
                  <Portrait
                    file={portrait.file}
                    alt={t(portrait.alt)}
                    sizes='(max-width: 700px) 44vw, (max-width: 1000px) 29vw, 15vw'
                  />
                </div>
                <figcaption>
                  <span>{t(portrait.label)}</span>
                  <small>{t(portrait.category)}</small>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className={styles.imageNote}>
            {t('Illustrative AI portraits. Your results will vary.')}
          </p>
        </section>

        <section
          id='how-it-works'
          className={styles.processSection}
          aria-labelledby='process-title'
        >
          <div className={`${styles.container} ${styles.processLayout}`}>
            <div>
              <p className={styles.eyebrow}>
                {t('LESS PREP. MORE POSSIBILITY.')}
              </p>
              <h2 id='process-title'>
                {t('Your camera roll.')}
                <br />
                <em>{t('Our starting point.')}</em>
              </h2>
              <ol className={styles.steps}>
                {[
                  [
                    'Upload a few photos',
                    'Choose 3–8 clear photos of yourself. Different angles and everyday lighting give the AI more to work with.',
                  ],
                  [
                    'Leave the setup to us',
                    'Our AI creates portraits with different outfits, backgrounds, and lighting. We’ll email you when they’re ready.',
                  ],
                  [
                    'Find your favorites',
                    'Choose the portraits that feel most like you, download them, and put them to work.',
                  ],
                ].map(([title, body], i) => (
                  <li key={title}>
                    <span className={styles.stepNumber}>0{i + 1}</span>
                    <div>
                      <h3>{t(title)}</h3>
                      <p>{t(body)}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <a href='#pricing' className={styles.textLink}>
                {t('Let’s make your headshots')}
                <Arrow />
              </a>
            </div>
            <div className={styles.processExample}>
              <div className={styles.exampleLabel}>
                <span>{t('A FEW EVERYDAY PHOTOS')}</span>
                <span>01</span>
              </div>
              <div className={styles.uploadPhotos}>
                {['upload-window', 'upload-outdoors', 'upload-home'].map(
                  (file, i) => (
                    <Portrait
                      key={file}
                      file={file}
                      alt={t(
                        'Casual reference photo {{number}} of the same woman',
                        { number: i + 1 },
                      )}
                      sizes='(max-width: 700px) 26vw, 12vw'
                    />
                  ),
                )}
              </div>
              <div className={styles.transformationLine}>
                <span />
                <Arrow />
                <span />
              </div>
              <div className={styles.exampleLabel}>
                <span>{t('A FRESH PROFESSIONAL LOOK')}</span>
                <span>02</span>
              </div>
              <div className={styles.resultPhotos}>
                <Portrait
                  file='studio-woman'
                  alt={t(
                    'The same woman in a charcoal blazer with studio lighting',
                  )}
                  sizes='(max-width: 700px) 40vw, 20vw'
                />
                <Portrait
                  file='woman-office'
                  alt={t(
                    'The same woman in an ivory blazer in a daylight office',
                  )}
                  sizes='(max-width: 700px) 40vw, 20vw'
                />
              </div>
              <p className={styles.imageNote}>
                {t('Illustrative AI transformation using a fictional person.')}
              </p>
            </div>
          </div>
        </section>

        <PricingSection appearance='studio' />

        <section
          className={`${styles.container} ${styles.faqSection}`}
          aria-labelledby='faq-title'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <div>
            <p className={styles.eyebrow}>{t('A LITTLE MORE DETAIL')}</p>
            <h2 id='faq-title'>
              {t('Good questions.')}
              <br />
              <em>{t('Straight answers.')}</em>
            </h2>
            <p>{t('Still have something on your mind?')}</p>
            <Link href='/contact' className={styles.textLink}>
              {t('Talk to us')}
              <Arrow />
            </Link>
          </div>
          <div className={styles.faqList}>
            {faqs.map((key) => (
              <details
                key={key}
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                <summary>
                  <span itemProp='name'>{t(`faq_${key}_question`)}</span>
                  <span className={styles.plus} aria-hidden='true' />
                </summary>
                <div
                  itemScope
                  itemProp='acceptedAnswer'
                  itemType='https://schema.org/Answer'
                >
                  <p itemProp='text'>{t(`faq_${key}_answer`)}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section
          className={`${styles.container} ${styles.closing}`}
          aria-labelledby='closing-title'
        >
          <div className={styles.closingPortrait}>
            <Portrait
              file='creative-woman'
              alt={t(
                'A confident professional portrait with a warm terracotta backdrop',
              )}
              sizes='(max-width: 700px) 25vw, 15vw'
            />
          </div>
          <div>
            <p className={styles.eyebrow}>
              {t('YOUR NEXT CHAPTER STARTS HERE')}
            </p>
            <h2 id='closing-title'>
              {t('Put your best face')}
              <br />
              <em>{t('forward.')}</em>
            </h2>
            <a href='#pricing' className={styles.primaryButton}>
              {t('Create my headshots')}
              <Arrow />
            </a>
            <p className={styles.purchaseNote}>
              {t('From $19. Yours to use everywhere.')}
            </p>
          </div>
          <span className={styles.closingMark} aria-hidden='true'>
            ↗
          </span>
        </section>
      </main>
      <Footer />
    </div>
  );
}
